import { c as _c } from "react/compiler-runtime";
import { IcoPencil16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
type InlineTextProps = {
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
  placeholder?: string;
  /** Non-editable bordered addon shown before the value, e.g. `outputs.`. */
  prefix?: string;
  mono?: boolean;
  canEdit?: boolean;
  /** Start in edit mode — used right after a row is added so the name is focused. */
  autoEdit?: boolean;
  /** Full-width, multi-line value instead of a single truncated line (e.g. a description). */
  wrap?: boolean;
  /** Regular-weight secondary text rather than a bold primary label (e.g. a description). */
  muted?: boolean;
  hasError?: boolean;
};

// Edits dispatch live (not buffered), so the node-level save-on-blur reads the
// latest value; this field's own blur only collapses back to the label and saves
// nothing.
const InlineText = t0 => {
  const $ = _c(51);
  const {
    value,
    onChange,
    ariaLabel,
    placeholder,
    prefix,
    mono,
    canEdit: t1,
    autoEdit: t2,
    wrap: t3,
    muted: t4,
    hasError: t5
  } = t0;
  const canEdit = t1 === undefined ? true : t1;
  const autoEdit = t2 === undefined ? false : t2;
  const wrap = t3 === undefined ? false : t3;
  const muted = t4 === undefined ? false : t4;
  const hasError = t5 === undefined ? false : t5;
  const [isEditing, setIsEditing] = useState(autoEdit);
  const inputRef = useRef(null);
  const textSize = muted ? "text-body-3" : "text-label-3";
  let t6;
  let t7;
  if ($[0] !== isEditing) {
    t6 = () => {
      if (!isEditing) {
        return;
      }
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    t7 = [isEditing];
    $[0] = isEditing;
    $[1] = t6;
    $[2] = t7;
  } else {
    t6 = $[1];
    t7 = $[2];
  }
  useEffect(t6, t7);
  let t8;
  if ($[3] !== canEdit) {
    t8 = () => {
      if (canEdit) {
        setIsEditing(true);
      }
    };
    $[3] = canEdit;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const open = t8;
  const handleKeyDown = _temp;
  let t9;
  if ($[5] !== hasError) {
    t9 = () => {
      if (hasError) {
        inputRef.current?.focus();
        return;
      }
      setIsEditing(false);
    };
    $[5] = hasError;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const handleBlur = t9;
  if (isEditing) {
    const t10 = wrap ? "flex w-full" : "inline-flex";
    let t11;
    if ($[7] !== t10) {
      t11 = cx("min-w-0 max-w-full", t10);
      $[7] = t10;
      $[8] = t11;
    } else {
      t11 = $[8];
    }
    let t12;
    if ($[9] !== prefix) {
      t12 = prefix && <span className="flex h-6 items-center rounded-l border border-r-0 border-tertiary bg-secondary px-2 text-snippet-3 text-tertiary font-mono">{prefix}</span>;
      $[9] = prefix;
      $[10] = t12;
    } else {
      t12 = $[10];
    }
    const t13 = hasError ? "border-error focus:border-error" : "border-tertiary focus:border-accent";
    const t14 = prefix ? "rounded-r" : "rounded";
    let t15;
    if ($[11] !== t13 || $[12] !== t14 || $[13] !== textSize) {
      t15 = cx("h-6 min-w-0 flex-1 border bg-primary px-2 text-primary outline-none", t13, textSize, t14);
      $[11] = t13;
      $[12] = t14;
      $[13] = textSize;
      $[14] = t15;
    } else {
      t15 = $[14];
    }
    let t16;
    if ($[15] !== onChange) {
      t16 = event_0 => onChange(event_0.target.value);
      $[15] = onChange;
      $[16] = t16;
    } else {
      t16 = $[16];
    }
    let t17;
    if ($[17] !== ariaLabel || $[18] !== handleBlur || $[19] !== placeholder || $[20] !== t15 || $[21] !== t16 || $[22] !== value) {
      t17 = <input ref={inputRef} aria-label={ariaLabel} className={t15} value={value} placeholder={placeholder} onChange={t16} onBlur={handleBlur} onKeyDown={handleKeyDown} />;
      $[17] = ariaLabel;
      $[18] = handleBlur;
      $[19] = placeholder;
      $[20] = t15;
      $[21] = t16;
      $[22] = value;
      $[23] = t17;
    } else {
      t17 = $[23];
    }
    let t18;
    if ($[24] !== t11 || $[25] !== t12 || $[26] !== t17) {
      t18 = <span className={t11}>{t12}{t17}</span>;
      $[24] = t11;
      $[25] = t12;
      $[26] = t17;
      $[27] = t18;
    } else {
      t18 = $[27];
    }
    return t18;
  }
  const isEmpty = value.trim().length === 0;
  const filledColor = muted ? "text-secondary" : "text-primary";
  const t10 = !canEdit;
  const t11 = wrap ? "flex w-full items-start" : "inline-flex max-w-full items-center";
  const t12 = canEdit && "hover:bg-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent";
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = cx("group/inline-text min-w-0 gap-1 rounded px-1 py-0.5 text-left", t11, t12);
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = wrap ? "min-w-0 flex-1 whitespace-pre-line [overflow-wrap:anywhere]" : "truncate";
  const t15 = mono && "font-mono!";
  const t16 = isEmpty ? "text-tertiary" : filledColor;
  let t17;
  if ($[31] !== t14 || $[32] !== t15 || $[33] !== t16 || $[34] !== textSize) {
    t17 = cx(textSize, t14, t15, t16);
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
    $[34] = textSize;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== prefix) {
    t18 = prefix && <span className="text-tertiary">{prefix}</span>;
    $[36] = prefix;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const t19 = isEmpty ? placeholder : value;
  let t20;
  if ($[38] !== t17 || $[39] !== t18 || $[40] !== t19) {
    t20 = <span className={t17}>{t18}{t19}</span>;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== canEdit) {
    t21 = canEdit && <span className="shrink-0 opacity-0 transition-opacity group-hover/inline-text:opacity-100"><IcoPencil16 color="tertiary" /></span>;
    $[42] = canEdit;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== ariaLabel || $[45] !== open || $[46] !== t10 || $[47] !== t13 || $[48] !== t20 || $[49] !== t21) {
    t22 = <button type="button" disabled={t10} aria-label={ariaLabel} onClick={open} className={t13}>{t20}{t21}</button>;
    $[44] = ariaLabel;
    $[45] = open;
    $[46] = t10;
    $[47] = t13;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  return t22;
};
export default InlineText;
function _temp(event) {
  if (event.key === "Enter" || event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.blur();
  }
}